import { useCallback, useEffect, useState } from "react";
import {
  getAdminRechargeRecords,
  type AdminRechargeRecord,
} from "../api/adminClient";

const inputClass =
  "px-4 py-2.5 border border-[var(--color-outline-variant)] rounded-lg text-sm bg-[var(--color-surface)] text-[var(--color-on-surface)] focus:outline-none focus:border-[var(--color-accent-blue)] focus:ring-1 focus:ring-[var(--color-accent-blue)] transition-colors transition-shadow duration-150";

const selectClass = inputClass;

const thClass = "pb-3 pr-4 font-medium text-left text-[var(--color-on-surface-variant)]";
const tdClass = "py-3 pr-4 text-[var(--color-on-surface)] vertical-middle align-middle";

function fmtCNY(n: number) {
  return `¥${n.toFixed(2)}`;
}

interface RechargePageProps {
  onAuthError?: () => void;
}

export default function RechargePage({ onAuthError }: RechargePageProps) {
  const [records, setRecords] = useState<AdminRechargeRecord[]>([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [userIdFilter, setUserIdFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const uid = userIdFilter.trim() ? parseInt(userIdFilter.trim()) : undefined;
      const data = await getAdminRechargeRecords({
        page,
        limit: 20,
        user_id: uid && !isNaN(uid) ? uid : undefined,
        status: statusFilter || undefined,
      });
      setRecords(data.items ?? []);
      setPages(data.pages);
      setTotal(data.total);
    } catch (e: unknown) {
      setRecords([]);
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      if (msg.includes("401") || msg.includes("403") || msg.includes("管理员")) {
        onAuthError?.();
      }
    } finally {
      setLoading(false);
    }
  }, [page, userIdFilter, statusFilter, onAuthError]);

  useEffect(() => {
    load();
  }, [load]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "paid":
        return (
          <span className="px-2 py-0.5 rounded text-xs bg-emerald-500/15 text-emerald-600 font-medium">
            已支付
          </span>
        );
      case "pending":
        return (
          <span className="px-2 py-0.5 rounded text-xs bg-amber-500/15 text-amber-600 font-medium animate-pulse">
            进行中
          </span>
        );
      case "failed":
        return (
          <span className="px-2 py-0.5 rounded text-xs bg-red-500/15 text-red-600 font-medium">
            失败
          </span>
        );
      case "expired":
        return (
          <span className="px-2 py-0.5 rounded text-xs bg-slate-200/60 text-slate-500 font-medium">
            已过期
          </span>
        );
      default:
        return (
          <span className="px-2 py-0.5 rounded text-xs bg-slate-100 text-slate-600 font-medium">
            {status}
          </span>
        );
    }
  };

  return (
    <div>
      <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1
            className="text-[28px] font-semibold text-[var(--color-on-surface)] mb-1"
            style={{ letterSpacing: "-0.02em" }}
          >
            充值记录
          </h1>
          <p className="text-[15px] text-[var(--color-on-surface-variant)]">
            查看所有用户充值明细，包含支付宝在线支付订单和系统赠送记录
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="px-4 py-2.5 rounded-lg text-sm font-medium bg-[var(--color-secondary-container)] text-white disabled:opacity-50"
        >
          {loading ? "加载中..." : "刷新"}
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-[var(--color-error-container)] text-[var(--color-on-error-container)] rounded-xl text-sm">
          {error}
        </div>
      )}

      <div className="bg-[var(--color-surface)] rounded-2xl p-6 border border-[var(--color-border-subtle)]">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex flex-wrap gap-3 items-center">
            <input
              type="text"
              placeholder="按用户ID过滤..."
              value={userIdFilter}
              onChange={(e) => {
                setUserIdFilter(e.target.value);
                setPage(1);
              }}
              className={`${inputClass} w-40`}
            />
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className={selectClass}
            >
              <option value="">全部状态</option>
              <option value="paid">已支付</option>
              <option value="pending">进行中</option>
              <option value="failed">失败</option>
              <option value="expired">已过期</option>
            </select>
          </div>
          {total > 0 && (
            <span className="text-sm font-normal text-[var(--color-on-surface-variant)]">
              共 {total} 条记录
            </span>
          )}
        </div>

        {records.length === 0 ? (
          <div className="py-12 text-center text-sm text-[var(--color-on-surface-variant)]">
            {loading ? "加载中..." : "暂无充值交易记录数据"}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b border-[var(--color-border-subtle)]">
                  <th className={thClass}>用户</th>
                  <th className={thClass}>订单号</th>
                  <th className={thClass}>金额</th>
                  <th className={thClass}>充值类型/备注</th>

                  <th className={thClass}>状态</th>
                  <th className={thClass}>创建时间</th>
                  <th className={thClass}>支付时间</th>
                </tr>
              </thead>
              <tbody>
                {records.map((row) => (
                  <tr
                     key={row.id}
                     className="border-b border-[var(--color-border-subtle)] last:border-0 hover:bg-[var(--color-surface-container-low)]"
                  >
                    <td className={tdClass}>
                      <div className="font-medium text-[var(--color-on-surface)]">{row.username}</div>
                      <div className="text-xs text-[var(--color-outline)]">ID: {row.user_id}</div>
                    </td>
                    <td className={`${tdClass} font-mono text-xs`}>
                      {row.order_no}
                    </td>
                    <td className={`${tdClass} font-semibold tabular-nums`}>
                      {fmtCNY(row.amount_cny)}
                    </td>
                    <td className={tdClass}>
                      <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                        row.order_no.startsWith("ADMIN_")
                          ? "bg-violet-500/10 text-violet-600"
                          : "bg-blue-500/10 text-blue-600"
                      }`}>
                        {row.description}
                      </span>
                    </td>

                    <td className={tdClass}>
                      {getStatusBadge(row.status)}
                    </td>
                    <td className={`${tdClass} text-xs text-[var(--color-on-surface-variant)]`}>
                      {row.created_at?.replace("T", " ").slice(0, 19) || "—"}
                    </td>
                    <td className={`${tdClass} text-xs text-[var(--color-on-surface-variant)]`}>
                      {row.paid_at?.replace("T", " ").slice(0, 19) || <span className="text-[var(--color-outline)]">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {pages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6">
            <button
              type="button"
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-3 py-1.5 rounded-lg text-sm bg-[var(--color-surface-container-low)] disabled:opacity-40"
            >
              上一页
            </button>
            <span className="text-sm text-[var(--color-on-surface-variant)] tabular-nums">
              {page} / {pages}
            </span>
            <button
              type="button"
              disabled={page >= pages || loading}
              onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1.5 rounded-lg text-sm bg-[var(--color-surface-container-low)] disabled:opacity-40"
            >
              下一页
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
